/**
 * React hook bridging the pure-math collision layer to the THREE / Zustand world.
 *
 * This is the ONLY place where THREE.Vector3 meets the AABB math — scene code
 * stays framework-ignorant below this line (aabb.ts + boundary.ts have zero
 * `three` import; test coverage is 100%).
 *
 * Usage (from WalkRig):
 *   const resolveProposed = useCollision();
 *   const resolved = resolveProposed(camera.position, proposedVec3);
 *   camera.position.copy(resolved);
 *
 * Side effect: writes `boundaryHit` to useNavStore on every call. BoundaryFeedback
 * (Plan 02) subscribes and renders the vignette automatically.
 */

import { useCallback } from 'react';
import * as THREE from 'three';
import { useNavStore } from '../store/useNavStore';
import { resolveMovement, type Vec3 } from './aabb';
import { isInsideBoundary, pushTowardCenter } from './boundary';
import { PLAYER_RADIUS } from '../lib/const';

export function useCollision() {
  const collisionBoxes = useNavStore((s) => s.collisionBoxes);
  const outerBoundary = useNavStore((s) => s.outerBoundary);
  const setBoundaryHit = useNavStore((s) => s.setBoundaryHit);

  /**
   * Given current + proposed camera positions (THREE.Vector3), resolve against
   * walls and boundary and return the resolved position.
   *
   * Writes `boundaryHit` to Zustand as a side effect — caller need not check.
   * Returns a NEW THREE.Vector3; does not mutate inputs.
   */
  const resolveProposed = useCallback(
    (current: THREE.Vector3, proposed: THREE.Vector3): THREE.Vector3 => {
      const c: Vec3 = [current.x, current.y, current.z];
      const p: Vec3 = [proposed.x, proposed.y, proposed.z];

      // 1. Wall collision (per-axis sliding with Minkowski inflation)
      let resolved = resolveMovement(c, p, collisionBoxes, PLAYER_RADIUS);

      // 2. Outer boundary check (NAV-07)
      if (outerBoundary) {
        // Use -PLAYER_RADIUS so the player is "outside" the moment their collision
        // cylinder touches the boundary edge, not when their camera point passes it.
        const inside = isInsideBoundary(resolved, outerBoundary, -PLAYER_RADIUS);
        setBoundaryHit(!inside);
        if (!inside) {
          resolved = pushTowardCenter(resolved, outerBoundary, 0.05);
        }
      }

      return new THREE.Vector3(resolved[0], resolved[1], resolved[2]);
    },
    [collisionBoxes, outerBoundary, setBoundaryHit]
  );

  return resolveProposed;
}
